import type { Json, SiteConfig } from "../types";

export interface FormTrackerOptions {
  emit: (eventName: string, props: Record<string, Json>) => void;
  getConfig: () => SiteConfig | null;
}

interface FormState {
  started: boolean;
  lastSubmitAt?: number;
}

// Callback widgets and AJAX forms often fire `submit` twice within a second
// (validation pass + real submit). One human submit = one form_submit.
const SUBMIT_DEDUPE_MS = 3000;

function getFormId(form: HTMLFormElement): string {
  const explicit = form.id || form.getAttribute("name") || form.getAttribute("data-form-id");
  if (explicit) return explicit.slice(0, 128);
  // Fallback for anonymous forms: a stable key that does NOT include the page
  // URL. WordPress themes often set action="<current url>", so the same form
  // used to get a different id per landing page and per UTM set.
  const cls = (form.className || "").trim().split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  const fields = Array.from(form.querySelectorAll<HTMLInputElement>("input[name], textarea[name], select[name]"))
    .map((el) => el.name)
    .filter((n) => n && n !== "s")
    .slice(0, 4)
    .join("+");
  const key = cls || fields || "anon";
  return ("form_" + key).slice(0, 128);
}

function fieldsCount(form: HTMLFormElement): number {
  return form.querySelectorAll("input:not([type=hidden]):not([type=submit]):not([type=button]), textarea, select").length;
}

/**
 * What kind of form this is. Site search, catalog filters, sort panels and
 * newsletter boxes are forms too, but submitting them is not a lead — reports
 * count only form_kind == "lead".
 *
 * The decision is made on a plain description of the form (`FormShape`) so it
 * can be unit-tested without a DOM: see pixel/test/form_kind.test.mjs.
 */
export type FormKind = "search" | "newsletter" | "lead";

export interface FormShape {
  /** raw `action` attribute, "" when absent */
  action: string;
  /** explicit `method` attribute, lower-cased; "" when absent (do not use the DOM default "get") */
  method: string;
  role: string;
  /** id + name + class, lower-cased */
  idName: string;
  /** data fields only: no hidden / submit / button / reset / checkbox */
  fields: Array<{ name: string; type: string }>;
  /** where the form lives */
  pageHost: string;
  pagePath: string;
}

const SEARCH_FIELD_NAMES = ["q", "query", "search", "s"];
// Catalog filters and sort panels: class="smartfilter", name="arrFilter_form",
// class="catalog-filter", class="catalog-panel-sort". The left boundary keeps
// words like "resort" out.
const FILTER_RE = /(^|[^a-z])(arrfilter|smartfilter|filter|sort)([^a-z]|$)/;
// Add-to-cart forms: WooCommerce `class="cart"` / `variations_form cart`. An order
// form that happens to carry "cart" in its class asks for a name or a phone, and stays a lead.
const CART_RE = /(^|[^a-z])cart([^a-z]|$)/;
const CONTACT_FIELD_RE = /phone|tel|name|fio|mail|addr|comment/;

function trimSlash(p: string): string {
  return p.length > 1 ? p.replace(/\/+$/, "") : p;
}

/** host ("" for a relative URL) and path of an action attribute, without query string and hash. */
function splitAction(action: string): { host: string; path: string } {
  const noQuery = action.split(/[?#]/)[0].trim().toLowerCase();
  const m = /^(?:[a-z][a-z0-9+.-]*:)?\/\/([^/]*)(\/.*)?$/.exec(noQuery);
  if (m) return { host: m[1], path: m[2] || "/" };
  return { host: "", path: noQuery };
}

export function classifyForm(f: FormShape): FormKind {
  const act = splitAction(f.action);
  // Bitrix and WordPress write the current page URL (with its query string)
  // into `action`. That says nothing about the form: on a /search/ results
  // page, or on a landing URL carrying "search" in its ad tags, a callback
  // form must stay a lead.
  const ownPage =
    !act.path ||
    ((act.host === "" || act.host === f.pageHost.toLowerCase()) &&
      trimSlash(act.path) === trimSlash(f.pagePath.toLowerCase()));
  const actionText = ownPage ? "" : act.host + act.path;
  const names = f.fields.map((x) => (x.name || "").toLowerCase());
  const types = f.fields.map((x) => (x.type || "text").toLowerCase());
  const hasContactType = types.some((t) => t === "tel" || t === "email");

  if (
    f.role === "search" ||
    /search|poisk/.test(f.idName) ||
    FILTER_RE.test(f.idName) ||
    /search|poisk/.test(actionText) ||
    types.indexOf("search") !== -1 ||
    // a lead form always asks for something; sort panels and add-to-cart forms have no data fields
    f.fields.length === 0 ||
    (CART_RE.test(f.idName) && !hasContactType && !names.some((n) => CONTACT_FIELD_RE.test(n))) ||
    (f.fields.length === 1 && ["q", "s", "search", "query", "text"].indexOf(names[0]) !== -1) ||
    // site search with extra selects: where + q, where + tags + how + q
    (!hasContactType && f.fields.length <= 4 && names.some((n) => SEARCH_FIELD_NAMES.indexOf(n) !== -1)) ||
    // an explicit GET form sends its fields in the URL: filters and searches do that, lead forms do not
    (f.method === "get" && !hasContactType)
  ) {
    return "search";
  }
  if (
    /subscri|podpis|newsletter|rassylk/.test(actionText + " " + f.idName) ||
    (f.fields.length === 1 && (types[0] === "email" || names[0] === "email"))
  ) {
    return "newsletter";
  }
  return "lead";
}

function shapeOf(form: HTMLFormElement): FormShape {
  const fields = Array.from(form.querySelectorAll<HTMLInputElement>("input, textarea, select"))
    .filter((el) => ["hidden", "submit", "button", "reset", "checkbox"].indexOf((el.type || "").toLowerCase()) === -1)
    .map((el) => ({ name: el.name || "", type: el.type || "text" }));
  return {
    action: form.getAttribute("action") || "",
    method: (form.getAttribute("method") || "").toLowerCase(),
    role: (form.getAttribute("role") || "").toLowerCase(),
    idName: ((form.id || "") + " " + (form.getAttribute("name") || "") + " " + (form.className || "")).toLowerCase(),
    fields,
    pageHost: location.host,
    pagePath: location.pathname,
  };
}

function formKind(form: HTMLFormElement): FormKind {
  return classifyForm(shapeOf(form));
}

function baseProps(form: HTMLFormElement): Record<string, Json> {
  return {
    form_kind: formKind(form),
    form_id: getFormId(form),
    form_method: (form.method || "get").toLowerCase().slice(0, 16),
    fields_count: fieldsCount(form),
  };
}

export function installFormTracker(opts: FormTrackerOptions): () => void {
  const seen = new WeakMap<HTMLFormElement, FormState>();

  function shouldTrack(): boolean {
    const c = opts.getConfig();
    return !(c && c.tracking && c.tracking.forms === false);
  }

  function onFocusIn(ev: FocusEvent) {
    if (!shouldTrack()) return;
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const form = (target as HTMLInputElement).form || target.closest("form");
    if (!form || !(form instanceof HTMLFormElement)) return;

    let state = seen.get(form);
    if (!state) {
      state = { started: false };
      seen.set(form, state);
    }
    if (state.started) return;
    state.started = true;
    opts.emit("form_start", baseProps(form));
  }

  function onSubmit(ev: SubmitEvent) {
    if (!shouldTrack()) return;
    const form = ev.target;
    if (!(form instanceof HTMLFormElement)) return;
    let state = seen.get(form);
    if (!state) {
      state = { started: false };
      seen.set(form, state);
    }
    const now = Date.now();
    if (state.lastSubmitAt !== undefined && now - state.lastSubmitAt < SUBMIT_DEDUPE_MS) return;
    state.lastSubmitAt = now;
    opts.emit("form_submit", baseProps(form));
  }

  function onInvalid(ev: Event) {
    if (!shouldTrack()) return;
    const el = ev.target as HTMLElement | null;
    if (!el) return;
    const form = (el as HTMLInputElement).form || el.closest("form");
    if (!form || !(form instanceof HTMLFormElement)) return;
    opts.emit("form_error", { ...baseProps(form), error_type: "browser_validation" });
  }

  document.addEventListener("focusin", onFocusIn, true);
  document.addEventListener("submit", onSubmit, true);
  // 'invalid' does not bubble — must capture.
  document.addEventListener("invalid", onInvalid, true);

  return () => {
    document.removeEventListener("focusin", onFocusIn, true);
    document.removeEventListener("submit", onSubmit, true);
    document.removeEventListener("invalid", onInvalid, true);
  };
}
